import React, { useState, useEffect, useRef } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { t } from '@lingui/core/macro';
import { useProject } from '../contexts/ProjectContext';
import { ReactComponent as ChevronDownIcon } from "../assets/chevron-down.svg";
import { ReactComponent as QuestionMarkCircleIcon } from "../assets/question-mark-circle.svg";
import { isAdminUserEmail } from "../utils/adminAccess";
import "../App.css";

export default function NavBar(): React.ReactElement {
  const [user, setUser] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProject, setSelectedProject } = useProject();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u: any) => setUser(u));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  const handleLogin = () => navigate("/login");

  const handleChangeProject = (): void => {
    setSelectedProject(null);
  };

  const isAdmin = isAdminUserEmail(user?.email);
  const isAdminRoute = location.pathname === "/admin";

  return (
    <nav className="navbar">
      <div className="navbar-title">{t`Credits Editor`}</div>
      <div className="navbar-right">
        {selectedProject && (
          <div className="navbar-project">
            <span className="project-name">{selectedProject.name}</span>
            <button onClick={handleChangeProject} className="change-project-button">
              Change Project
            </button>
          </div>
        )}
        <a className="navbar-support" href="mailto:support@fintitles.com" target="_blank" rel="noreferrer">
          <QuestionMarkCircleIcon
            className="navbar-support-icon"
            width={16}
            height={16}
            aria-hidden="true"
            focusable="false"
          />
          {t`Email support`}
        </a>
        {isAdmin && (
          <button
            className={`nav-admin-button ${isAdminRoute ? "active" : ""}`}
            onClick={() => navigate('/admin')}
          >
            Admin
          </button>
        )}
        {/* <LanguageSwitcher /> */}
        <div className="navbar-user" ref={dropdownRef}>
          <button onClick={() => setOpen(!open)} className="user-button">
            {user ? user.email : "Guest"}
            <ChevronDownIcon
              className={`dropdown-arrow ${open ? "open" : ""}`}
              width={14}
              height={14}
              style={{ marginLeft: "6px" }}
              aria-hidden="true"
              focusable="false"
            />
          </button>
          {open && (
            <div className="dropdown">
              {user ? (
                <button onClick={handleLogout} className="dropdown-item">
                  Logout
                </button>
              ) : (
                <button onClick={handleLogin} className="dropdown-item">
                  Login / Sign Up
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
